import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";

/**
 * Sortable list wrapper. `items` is an array of objects each with an `id` prop.
 * `renderItem(item, { dragHandleProps })` should return the visual element,
 * spreading dragHandleProps onto the drag handle element.
 */
export function SortableList({ items, onReorder, renderItem, idKey = "id" }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((i) => i[idKey] === active.id);
    const newIdx = items.findIndex((i) => i[idKey] === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    onReorder(arrayMove(items, oldIdx, newIdx));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items.map((i) => i[idKey])} strategy={verticalListSortingStrategy}>
        <div className="space-y-4">
          {items.map((item) => (
            <SortableWrapper key={item[idKey]} id={item[idKey]} render={renderItem} item={item} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function SortableWrapper({ id, render, item }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 20 : "auto",
    boxShadow: isDragging ? "0 12px 32px rgba(0,0,0,0.15)" : undefined,
  };
  const dragHandleProps = {
    ...attributes,
    ...listeners,
    "data-testid": `drag-handle-${id}`,
    className: "cursor-grab active:cursor-grabbing p-1.5 text-gray-400 hover:text-[#538135] transition-colors",
    title: "Drag to reorder",
    children: <GripVertical size={16} />,
  };
  return (
    <div ref={setNodeRef} style={style}>
      {render(item, { dragHandleProps })}
    </div>
  );
}
